import React, { FC, useCallback, useEffect, useMemo } from "react";
import { Permissions, TableInfoDc } from "@evergis/api";
import { AccessControlListDc } from "@evergis/api/dist/__generated__/data-contracts";

import { Chips } from "./Chips";

import { layerInfosWithDeps } from "../../../../resources/layers";
import { updatePermissions } from "../../../../resources/bulkOperations";
import { isConfigure } from "../../../../utils/permissions";
import { useResultNotifications } from "../hooks/useResultNotifications";

interface LayerWmsProps {
  layerName: string;
  setIsLoading: (state: boolean) => void;
}

export const LayerWms: FC<LayerWmsProps> = ({ layerName, setIsLoading }) => {
  const {
    data: layersInfo,
    loading,
    refetch,
  } = layerInfosWithDeps.use({ input: [layerName], waitInput: true, immediate: false });

  const layerInfo = useMemo(() => layersInfo?.layers[0], [layersInfo]);
  const tables: (TableInfoDc & { acl?: AccessControlListDc })[] = useMemo(() => layersInfo?.deps.tables, [layersInfo]);

  const [update, { data }] = updatePermissions.use();

  const unavailableResources = useMemo(() => {
    const result = [];

    tables?.forEach(table => {
      if (!isConfigure(table.permissions)) {
        result.push(table);
      }
    });

    return result;
  }, [tables]);

  const getPermissionsInput = useCallback(
    (role: string, type: "add" | "remove") => ({
      tables: tables.map(table => ({
        objectName: table.name,
        data:
          type === "add"
            ? [
                ...table.acl.data.filter(item => item.role !== role),
                {
                  role,
                  permissions: Permissions.Read,
                },
              ]
            : [...table.acl.data.filter(item => item.role !== role)],
      })),
      layers: [
        {
          objectName: layerName,
          data:
            type === "add"
              ? [
                  ...layerInfo?.configuration.acl.data.filter(item => item.role !== role),
                  {
                    role,
                    permissions: Permissions.Read,
                  },
                ]
              : [...layerInfo?.configuration.acl.data.filter(item => item.role !== role)],
        },
      ],
    }),
    [layerInfo?.configuration.acl.data, layerName, tables],
  );

  const onAdd = useCallback(
    (role: string) => {
      update({
        input: getPermissionsInput(role, "add"),
      }).then(refetch);
    },
    [update, getPermissionsInput, refetch],
  );

  const onRemove = useCallback(
    (role: string) => {
      update({
        input: getPermissionsInput(role, "remove"),
      }).then(refetch);
    },
    [update, getPermissionsInput, refetch],
  );

  useEffect(() => {
    refetch();
  }, []);

  useResultNotifications(data && unavailableResources.length > 0, unavailableResources);

  return (
    <Chips
      resourceLoading={loading}
      resourceAcl={layerInfo?.configuration.acl.data}
      onAdd={onAdd}
      onRemove={onRemove}
      setIsLoading={setIsLoading}
    />
  );
};
